import { useLogger } from '@internal/ui-common';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';
import { DepartmentCostSummary, Team } from '@internal/schemas-common';

export function useDepartmentCostSummary(teams: Team[]) {
	const fetchApi = useApi(fetchApiRef);
	const discoveryApi = useApi(discoveryApiRef);
	const logger = useLogger();

	const [data, setData] = useState<DepartmentCostSummary | null>(null);
	const [isLoading, setLoading] = useState(!!teams);
	const [error, setError] = useState<Error | null>(null);

	const teamNames = teams.map(team => team.nameInGitlab).join(',');

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		const fetchData = async () => {
			try {
				setLoading(true);
				setError(null);

				const baseUrl = await discoveryApi.getBaseUrl('zf-cost');
				const result = await fetchApi.fetch(`${baseUrl}/teams/cost-summaries?teams=${teamNames}`, { signal });

				if (!result.ok) throw new Error(`API error: ${result.status} ${result.statusText}`);

				const json = await result.json();
				setData(json);
			} catch (catchError) {
				if (signal.aborted) return;
				logger.error('Error fetching cost summary of departments', catchError);
				setError(new Error("Something went wrong while getting the cost data."));
				setData(null);
			} finally {
				if (!signal.aborted) setLoading(false);
			}
		};

		fetchData();

		return () => controller.abort();
	}, [teamNames, discoveryApi, fetchApi, logger]);

	return { value: data, isLoading, error };
}
